import { useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { RELOAD_KEY } from "../../store/user/contextChoice";
import {
  ACTIVE_ENROLLMENT,
  CONTEXT_AVAILABILITY,
  fetchActiveChallenge,
  fetchExperienceContexts,
  fetchMission,
  markContextUnavailable,
  selectActiveContext,
  selectActiveEnrollmentStatus,
  selectActiveSquadId,
  selectContextAvailability,
  selectContextDiscoveryStatus,
  selectContextGeneration,
  selectEnrollmentMismatchCohortId,
  selectSwitchableContexts,
} from "../../store/user/slice";
import {
  ExperienceChooserScreen,
  ExperienceSwitcherModal,
} from "./ExperienceChooser";

/* M2: RELOAD ON A CONTEXT CHANGE.
   Per-reducer guards kept missing late replies for an earlier context (task
   updates, plan-session writes, callbacks after an await). So when the active
   context changes after the first accepted settle of a page load, the page
   reloads and every slice, request and closure starts again from the new
   context. Module-level, because this is per page load, not per mount. */
const RELOAD_WINDOW_MS = 30000;
let pageLoadSettle = null;
let reloadedThisPageLoad = false;

/** Test-only: forget this page load's first settle and reload. */
export const __resetContextReloadForTests = () => {
  pageLoadSettle = null;
  reloadedThisPageLoad = false;
};

/**
 * Reloads for one context change, unless ANY reload was recorded for this
 * user in the last 30 seconds (then "held": the caller shows unavailable
 * instead, so neither a repeat nor an alternating answer can loop). If
 * session storage cannot be used the page still reloads, but at most once per
 * page load.
 */
const reloadForContextChange = ({ userId, from, to }) => {
  const now = Date.now();
  try {
    const raw = window.sessionStorage.getItem(RELOAD_KEY);
    const last = raw ? JSON.parse(raw) : null;
    if (last && last.userId === userId && now - last.at < RELOAD_WINDOW_MS) {
      return "held";
    }
    window.sessionStorage.setItem(
      RELOAD_KEY,
      JSON.stringify({ userId, from, to, at: now }),
    );
  } catch (_error) {
    // No storage: fall through to the once-per-page-load flag below.
  }
  if (!reloadedThisPageLoad) {
    reloadedThisPageLoad = true;
    window.location.reload();
  }
  return "reload";
};

/**
 * M2: THE ONE DISCOVERY OWNER.
 *
 * Mounted once by Layout for every student route (r2 disposition M1), so a
 * direct entry to any page discovers the learner's delivery contexts exactly
 * once per session. It runs again only when asked: after a join
 * (`requestContextRediscovery`) or after a mission read returns 404 for the
 * active squad (the slice spends that squad's one rediscovery). A sign out
 * resets the state to idle, so the next account discovers its own.
 *
 * It also:
 *   - reloads the page when an accepted discovery settle names a different
 *     active squad than the first accepted settle of this page load (X to Y,
 *     X to none, none to X), and when the signed-in user is not the one
 *     discovery ran for. The first settle never reloads. A stale or
 *     superseded reply is never accepted by the slice, so it never reloads;
 *   - reads the active context's own challenge once per context when `/me`
 *     names another cohort (experience switcher B1; this replaced M2's one
 *     `/me` refresh). The gate shows a skeleton until it lands;
 *   - reads the active context's mission once per context when the store
 *     holds none for it, on every route. On the Dashboard this duplicates the
 *     page's own bootstrap read once; replies are take-latest.
 *
 * `enabled` and `userId` are decided by the caller (an authenticated,
 * non-staff session), which keeps this component free of the auth provider.
 */
export const ExperienceContextOwner = ({ enabled, userId = null }) => {
  const dispatch = useDispatch();
  const status = useSelector(selectContextDiscoveryStatus);
  const rediscoveryRequested = useSelector(
    (state) => state.user?.experienceContext?.rediscoveryRequested === true,
  );
  const discoveredFor = useSelector(
    (state) => state.user?.experienceContext?.userId ?? null,
  );
  const settleCount = useSelector(
    (state) => state.user?.experienceContext?.settleCount ?? 0,
  );
  const activeSquadId = useSelector(selectActiveSquadId);
  const generation = useSelector(selectContextGeneration);
  const missionSquadId = useSelector(
    (state) => state.user?.mission?.context?.squad_id ?? null,
  );
  const missionErrorSquadId = useSelector(
    (state) => state.user?.missionError?.squadId ?? null,
  );
  const mismatchCohortId = useSelector(selectEnrollmentMismatchCohortId);
  const activeChallengeId = useSelector(
    (state) => selectActiveContext(state)?.challenge_id ?? null,
  );
  const chosenSquadId = useSelector(
    (state) => state.user?.experienceContext?.chosenSquadId ?? null,
  );
  const signedInUserId =
    userId === null || userId === undefined ? null : Number(userId);
  const userChanged =
    signedInUserId !== null &&
    discoveredFor !== null &&
    discoveredFor !== signedInUserId;

  // Another learner never inherits this context: a signed-in user other than
  // the one discovery ran (or is running) for reloads the page.
  useEffect(() => {
    if (!enabled || !userChanged) return;
    const outcome = reloadForContextChange({
      userId: signedInUserId,
      from: `user:${discoveredFor}`,
      to: `user:${signedInUserId}`,
    });
    if (outcome === "held") dispatch(markContextUnavailable());
  }, [enabled, userChanged, signedInUserId, discoveredFor, dispatch]);

  useEffect(() => {
    if (!enabled || userChanged || status === "loading") return;
    if (status === "idle" || rediscoveryRequested) {
      dispatch(fetchExperienceContexts({ userId: signedInUserId }));
    }
  }, [
    enabled,
    userChanged,
    status,
    rediscoveryRequested,
    signedInUserId,
    dispatch,
  ]);

  // An undiscovered (idle) slice is a new session: the page load itself, or a
  // sign out, which resets the store. Its first accepted settle is the new
  // baseline, so the next learner on a shared machine is not reloaded.
  //
  // Each accepted settle, once: the first of the page load is recorded, and
  // any later one naming a different active squad reloads the page.
  //
  // `handledSettle` starts at the count this owner MOUNTS with, so a remount
  // (an auth route and back, a kickoff) never reads a settle it did not see
  // land as a change. A mount that finds no baseline but a settled slice takes
  // the settled squad as the baseline. A reset (idle, or a count that went
  // down) clears it, so the next settle is the new baseline and not skipped.
  const handledSettle = useRef(settleCount);
  useEffect(() => {
    if (
      pageLoadSettle === null &&
      handledSettle.current > 0 &&
      (status === "ready" || status === "unavailable")
    ) {
      pageLoadSettle = { squadId: activeSquadId };
    }
    // Mount only: what the slice already holds when this owner arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (status === "idle" || settleCount < handledSettle.current) {
      pageLoadSettle = null;
      handledSettle.current = 0;
    }
  }, [status, settleCount]);

  useEffect(() => {
    if (!enabled || userChanged || settleCount === 0) return;
    if (handledSettle.current === settleCount) return;
    handledSettle.current = settleCount;
    if (pageLoadSettle === null) {
      pageLoadSettle = { squadId: activeSquadId };
      return;
    }
    if (pageLoadSettle.squadId === activeSquadId) return;
    const outcome = reloadForContextChange({
      userId: signedInUserId ?? discoveredFor,
      from: pageLoadSettle.squadId,
      to: activeSquadId,
    });
    if (outcome === "held") dispatch(markContextUnavailable());
  }, [
    enabled,
    userChanged,
    settleCount,
    activeSquadId,
    signedInUserId,
    discoveredFor,
    dispatch,
  ]);

  // An explicit in-memory choice (storage blocked, nothing active before) is
  // the learner's own: it becomes this page load's baseline, so a later
  // rediscovery that selects the same squad is not read as a change.
  useEffect(() => {
    if (chosenSquadId === null || chosenSquadId !== activeSquadId) return;
    if (pageLoadSettle !== null) pageLoadSettle = { squadId: chosenSquadId };
  }, [chosenSquadId, activeSquadId]);

  // B1: once per context, the active context's own challenge when `/me`
  // names another cohort. An ambiguous challenge (null id) is never guessed.
  const challengeReadFor = useRef(null);
  useEffect(() => {
    if (!enabled || userChanged || mismatchCohortId === null) return;
    if (activeSquadId === null || activeChallengeId === null) return;
    const key = `${generation}:${activeSquadId}:${activeChallengeId}`;
    if (challengeReadFor.current === key) return;
    challengeReadFor.current = key;
    dispatch(
      fetchActiveChallenge({
        squadId: activeSquadId,
        challengeId: activeChallengeId,
      }),
    );
  }, [
    enabled,
    userChanged,
    mismatchCohortId,
    activeSquadId,
    activeChallengeId,
    generation,
    dispatch,
  ]);

  const missionReadFor = useRef(null);
  useEffect(() => {
    if (!enabled || userChanged || activeSquadId === null) return;
    if (Number(missionSquadId) === activeSquadId) return;
    if (missionErrorSquadId === activeSquadId) return;
    if (missionReadFor.current === generation) return;
    missionReadFor.current = generation;
    dispatch(fetchMission({ squadId: activeSquadId }));
  }, [
    enabled,
    userChanged,
    activeSquadId,
    generation,
    missionSquadId,
    missionErrorSquadId,
    dispatch,
  ]);

  return null;
};

const Skeleton = () => (
  <div
    className="flex w-full flex-col gap-4 p-6"
    role="status"
    aria-live="polite"
    aria-busy="true"
    data-testid="experience-context-pending"
  >
    <span className="sr-only">loading your mission</span>
    <div className="h-6 w-1/3 max-w-[240px] rounded-md bg-background-cloudburst animate-shimmer motion-reduce:animate-none" />
    <div className="h-32 w-full rounded-lg bg-background-cloudburst animate-shimmer motion-reduce:animate-none" />
    <div className="h-32 w-full rounded-lg bg-background-cloudburst animate-shimmer motion-reduce:animate-none" />
  </div>
);

const Message = ({
  testId,
  title,
  body,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
}) => (
  <div
    className="flex min-h-[50vh] w-full items-center justify-center p-6"
    data-testid={testId}
  >
    <div className="flex max-w-md flex-col items-center gap-3 text-center">
      <p className="text-lg text-text-lv1">{title}</p>
      <p className="text-sm text-text-lv3">{body}</p>
      {actionLabel || secondaryLabel ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
          {actionLabel ? (
            <button
              type="button"
              onClick={onAction}
              className="focus-ring rounded-full bg-background-biscay px-6 py-2 text-sm text-text-lv1 transition-all duration-fast hover:bg-background-astronaut"
            >
              {actionLabel}
            </button>
          ) : null}
          {secondaryLabel ? (
            <button
              type="button"
              onClick={onSecondary}
              aria-haspopup="dialog"
              className="focus-ring rounded-full px-6 py-2 text-sm text-charlotte transition-all duration-fast hover:bg-white/8"
            >
              {secondaryLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  </div>
);

const SWITCH_LABEL = "switch experience";

/**
 * M2: THE PLAIN STATES A MISSION-DEPENDENT PAGE SHOWS BEFORE ITS CONTENT.
 *
 * pending: a skeleton until discovery settles (rehydrated mission and squad
 * rows are not trusted before then). failed: retryable, never read as zero
 * contexts. unavailable: joined, but no single active context, or the server
 * refused this one (409, or 404 after its one rediscovery). mission_failed:
 * the active context's mission read failed and there is none to show yet.
 * not_joined and ready render the page as today.
 *
 * Experience switcher: a learner with two or more discovered contexts and
 * none chosen sees the chooser instead of "unavailable". In every other
 * unavailable or failed state the chooser stays one button away, so a
 * refused or failing choice can never strand them. A single-context or
 * zero-context learner sees exactly the M2 states.
 */
export const ExperienceContextGate = ({ children }) => {
  const dispatch = useDispatch();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const discoveredFor = useSelector(
    (state) => state.user?.experienceContext?.userId ?? null,
  );
  const discoveryStatus = useSelector(selectContextDiscoveryStatus);
  const hasActive = useSelector(
    (state) => !!state.user?.experienceContext?.active,
  );
  const availability = useSelector(selectContextAvailability);
  const squadId = useSelector(selectActiveSquadId);
  const activeContext = useSelector(selectActiveContext);
  const enrollmentStatus = useSelector(selectActiveEnrollmentStatus);
  const switchable = useSelector(selectSwitchableContexts).length > 0;

  const switcher = switchable
    ? {
        secondaryLabel: SWITCH_LABEL,
        onSecondary: () => setSwitcherOpen(true),
      }
    : {};
  const withSwitcher = (content) => (
    <>
      {content}
      {switchable ? (
        <ExperienceSwitcherModal
          isOpen={switcherOpen}
          onClose={() => setSwitcherOpen(false)}
        />
      ) : null}
    </>
  );

  if (availability === CONTEXT_AVAILABILITY.PENDING) return <Skeleton />;
  if (availability === CONTEXT_AVAILABILITY.FAILED) {
    return withSwitcher(
      <Message
        testId="experience-context-failed"
        title="we couldn't load your mission."
        body="check your connection and try again."
        actionLabel="try again"
        onAction={() =>
          dispatch(fetchExperienceContexts({ userId: discoveredFor }))
        }
        {...switcher}
      />,
    );
  }
  if (availability === CONTEXT_AVAILABILITY.MISSION_FAILED) {
    return withSwitcher(
      <Message
        testId="experience-context-mission-failed"
        title="we couldn't load your mission."
        body="check your connection and try again."
        actionLabel="try again"
        onAction={() => {
          if (squadId === null) return;
          if (
            enrollmentStatus === ACTIVE_ENROLLMENT.FAILED &&
            activeContext?.challenge_id
          ) {
            dispatch(
              fetchActiveChallenge({
                squadId,
                challengeId: activeContext.challenge_id,
              }),
            );
            return;
          }
          dispatch(fetchMission({ squadId }));
        }}
        {...switcher}
      />,
    );
  }
  if (availability === CONTEXT_AVAILABILITY.UNAVAILABLE) {
    if (switchable && discoveryStatus === "ready" && !hasActive) {
      return <ExperienceChooserScreen />;
    }
    if (switchable) {
      return withSwitcher(
        <Message
          testId="experience-context-unavailable"
          title="this experience isn't available right now."
          body="pick another one, or ask your facilitator."
          {...switcher}
        />,
      );
    }
    return (
      <Message
        testId="experience-context-unavailable"
        title="your mission isn't available here right now."
        body="ask your facilitator which squad and season you're working in."
      />
    );
  }
  return children;
};
